/**
 * Cytoscape.js Stylesheet - Icon-based with Uniform Colors
 *
 * Uses Lucide icons and consistent color scheme for all nodes.
 * Supports dark and light themes.
 */

import {
  ENTITY_ICON_COMPONENTS,
  ENTITY_TYPE_COLORS,
  type GraphTheme,
  getNodeColors,
  MINIMAL_NODE_TYPES,
} from './entity-icons'
import { nodeIconDataUri } from './node-icons'
import { PASSAGE_LABEL } from './types'

// Type for Cytoscape stylesheet entry
interface StyleEntry {
  selector: string
  style: Record<string, unknown>
}

/**
 * Generate per-entity-type color styles from ENTITY_TYPE_COLORS.
 * Applied to ALL typed nodes, regardless of whether they have an icon.
 */
function generateEntityColorStyles(): StyleEntry[] {
  const styles: StyleEntry[] = []
  const skipTypes = new Set(['Default'])

  Object.entries(ENTITY_TYPE_COLORS).forEach(([typeLabel, colors]) => {
    if (skipTypes.has(typeLabel)) return
    styles.push({
      selector: `node[typeLabel="${typeLabel}"]`,
      style: {
        'background-color': colors.bg,
        'border-color': colors.border,
      },
    })
  })

  return styles
}

/**
 * Mapped types that get no generic icon rule: Default is the fallback key, not
 * a type; minimal types are bare dots; notes carry their text, pictures their
 * image and transcript passages their timecode. A Photo keeps its glyph: the
 * node of a photo two identified people share has no image of its own, and one
 * that has an image draws it over the glyph (the image rule comes later).
 */
const NO_ICON_RULE_TYPES = new Set([
  'Default',
  ...MINIMAL_NODE_TYPES,
  'Note',
  'StickyNote',
  'Picture',
  PASSAGE_LABEL,
])

/** The app's `--font-mono` (index.css): the canvas cannot read the variable. */
const MONO_FONT = 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace'

/**
 * The label of a transcript passage: its span ("12:30 – 13:42") inside the
 * chip, in mono so every chip of a recording is cut to one measure. Shared by
 * the passage's own rule and by the one that takes the label back from the
 * zoom-driven type label. The chip is sized for this font in types.ts.
 */
function passageLabelStyle(textColor: string): Record<string, unknown> {
  return {
    label: 'data(displayName)',
    'text-valign': 'center',
    'text-halign': 'center',
    'text-margin-y': 0,
    'font-family': MONO_FONT,
    'font-size': '10px',
    'font-weight': 500,
    color: textColor,
    'text-background-opacity': 0,
    'text-wrap': 'none',
  }
}

function generateIconStyles(iconColor: string): StyleEntry[] {
  return Object.keys(ENTITY_ICON_COMPONENTS)
    .filter(typeLabel => !NO_ICON_RULE_TYPES.has(typeLabel))
    .map(typeLabel => ({
      selector: `node[typeLabel="${typeLabel}"]`,
      style: {
        'background-image': nodeIconDataUri(typeLabel, iconColor),
        'background-fit': 'none',
        'background-width': '22px',
        'background-height': '22px',
        'background-opacity': 1,
        'background-position-x': '50%',
        'background-position-y': '50%',
      },
    }))
}

/**
 * Create a theme-aware Cytoscape stylesheet.
 */
export function createSpectreStylesheet(theme: GraphTheme = 'dark'): StyleEntry[] {
  const c = getNodeColors(theme)

  return [
    // ═══════════════════════════════════════════════════════════════════════════
    // BASE NODE STYLES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node',
      style: {
        shape: 'ellipse',
        width: 'data(baseWidth)',
        height: 'data(baseHeight)',
        'border-width': 2,
        'background-color': c.bg,
        'border-color': c.border,
        color: c.text,
        label: '',
        'transition-property': 'background-color, border-color, width, height, opacity',
        'transition-duration': '0.15s',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // EVENT NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="Event"]',
      style: {
        label: 'data(dateLabel)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 10,
        'font-size': '12px',
        'font-weight': 700,
        color: c.eventTextColor,
        'text-background-color': c.eventTextBg,
        'text-background-opacity': 0.95,
        'text-background-padding': 4,
        'text-background-shape': 'roundrectangle',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NOTE NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="Note"], node[typeLabel="StickyNote"]',
      style: {
        'background-color': '#fef3c7', // amber-100 (works in both themes)
        'border-color': '#f59e0b',
        'border-width': 2,
        label: 'data(displayName)',
        'text-valign': 'center',
        'text-halign': 'center',
        'text-wrap': 'wrap',
        'text-max-width': '80px',
        'font-size': '11px',
        'font-weight': 600,
        color: '#78350f',
        'text-background-opacity': 0,
        shape: 'roundrectangle',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // FINDING NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="Finding"]',
      style: {
        'background-color': '#fef9c3',
        'border-color': '#eab308',
        'border-width': 2,
        label: 'data(displayName)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 5,
        'text-wrap': 'wrap',
        'text-max-width': '100px',
        'font-size': '10px',
        'font-weight': 500,
        color: '#a16207',
        shape: 'diamond',
        width: 40,
        height: 40,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENT NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="Document"]',
      style: {
        width: 55,
        height: 55,
        shape: 'barrel',
        'background-color': c.docBg,
        'border-color': c.docBorder,
        'border-width': 3,
        'background-image': nodeIconDataUri('Document', c.docIconColor),
        'background-fit': 'none',
        'background-width': '30px',
        'background-height': '30px',
        'background-opacity': 1,
        'background-position-x': '50%',
        'background-position-y': '50%',
        'overlay-color': c.docOverlay,
        'overlay-padding': 4,
        'overlay-opacity': 0.15,
        label: 'data(shortDocLabel)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 10,
        'font-size': '10px',
        'font-weight': 500,
        color: c.docTextColor,
        'text-wrap': 'wrap',
        'text-max-width': '120px',
        'text-background-color': c.docTextBg,
        'text-background-opacity': 0.9,
        'text-background-padding': 4,
        'text-background-shape': 'roundrectangle',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SOURCE DOCUMENT NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="Document"][?isSource]',
      style: {
        'background-color': c.sourceDocBg,
        'border-color': c.sourceDocBorder,
        'overlay-color': c.sourceDocOverlay,
        color: c.sourceDocTextColor,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // PICTURE NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="Picture"], node[typeLabel="Photo"]',
      style: {
        shape: 'roundrectangle',
        'border-width': 3,
        'border-color': c.pictureBorder,
        'background-color': c.pictureBg,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // MINIMAL NODES - Chunks, Documents, LogEvents
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector:
        'node[typeLabel="Chunk"], node[typeLabel="Document"], node[typeLabel="CaseDocument"], node[typeLabel="LogEvent"]',
      style: {
        width: 6,
        height: 6,
        'background-color': c.minimalBg,
        'border-color': c.minimalBorder,
        'border-width': 1,
        label: '',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // ENTITY TYPE COLORS
    // ═══════════════════════════════════════════════════════════════════════════
    ...generateEntityColorStyles(),

    // ═══════════════════════════════════════════════════════════════════════════
    // ICON STYLES
    // ═══════════════════════════════════════════════════════════════════════════
    ...generateIconStyles(c.iconColor),

    // ═══════════════════════════════════════════════════════════════════════════
    // NODES WITH IMAGES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[imageUrl]',
      style: {
        'background-image': 'data(imageUrl)',
        'background-fit': 'cover',
        'background-clip': 'node',
        'border-width': 3,
        'background-width': '100%',
        'background-height': '100%',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // FACE NODES — the crop fills a compact square (one case holds hundreds)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="Face"]',
      style: {
        shape: 'roundrectangle',
        width: 34,
        height: 34,
        'border-width': 2,
        label: '',
      },
    },
    {
      selector: 'node[typeLabel="Face"][imageUrl]',
      style: {
        'background-image': 'data(imageUrl)',
        'background-fit': 'cover',
        'background-clip': 'node',
        'background-width': '100%',
        'background-height': '100%',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TRANSCRIPT PASSAGE NODES — a timecode, not an entity. A recording holds
    // dozens: each is a flat chip with its span inside, in the passage hue
    // (ENTITY_TYPE_COLORS) washed down to a tint, so a ring of them reads as a
    // ruler around the recording and never outweighs the people it mentions.
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[typeLabel="TranscriptPassage"]',
      style: {
        shape: 'round-rectangle',
        'border-width': 1.5,
        'background-opacity': 0.22,
        ...passageLabelStyle(c.eventTextColor),
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SUSPECT NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.suspect, node[?isSuspect]',
      style: {
        'background-color': c.suspectBg,
        'border-color': c.suspectBorder,
        'border-width': 3,
        label: 'data(name)',
        'font-size': 11,
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 4,
        color: c.suspectTextColor,
        'text-background-color': c.suspectTextBg,
        'text-background-opacity': 0.7,
        'text-background-padding': 3,
        'text-background-shape': 'roundrectangle',
      },
    },

    {
      selector: 'node.suspect:selected, node[?isSuspect]:selected',
      style: {
        'background-color': c.suspectBg,
        'border-color': c.suspectBorder,
        'border-width': 4,
        'overlay-color': c.suspectBorder,
        'overlay-padding': 6,
        'overlay-opacity': 0.3,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // CRIME-RELATED NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.crime-related, node[?isCrimeRelated]',
      style: {
        'background-color': c.crimeRelatedBg,
        'border-color': c.crimeRelatedBorder,
        'border-width': 3,
        label: 'data(name)',
        'font-size': 11,
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 4,
        color: c.suspectTextColor,
        'text-background-color': c.suspectTextBg,
        'text-background-opacity': 0.7,
        'text-background-padding': 3,
        'text-background-shape': 'roundrectangle',
      },
    },

    {
      selector: 'node.crime-related:selected, node[?isCrimeRelated]:selected',
      style: {
        'background-color': c.crimeRelatedBg,
        'border-color': c.crimeRelatedBorder,
        'border-width': 4,
        'overlay-color': c.crimeRelatedBorder,
        'overlay-padding': 6,
        'overlay-opacity': 0.3,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // BRIDGE NODES — orange glow for cross-cluster bridges
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node[?isBridge]',
      style: {
        'border-color': '#ea580c', // orange-600
        'border-width': 3,
        'overlay-color': '#f97316', // orange-500
        'overlay-padding': 8,
        'overlay-opacity': 0.35,
      },
    },
    {
      selector: 'node[?isBridge]:selected',
      style: {
        'border-color': '#ea580c',
        'border-width': 4,
        'overlay-color': '#f97316',
        'overlay-padding': 10,
        'overlay-opacity': 0.55,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // HOVER STATE
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node:active',
      style: {
        'overlay-color': c.hoverBg,
        'overlay-padding': 4,
        'overlay-opacity': 0.3,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SELECTED STATE
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node:selected',
      style: {
        'background-color': c.selectedBg,
        'border-color': c.selectedBorder,
        'border-width': 4,
        'overlay-color': c.selectedBg,
        'overlay-padding': 6,
        'overlay-opacity': 0.2,
      },
    },

    {
      selector:
        'node[typeLabel="Chunk"]:selected, node[typeLabel="Document"]:selected, node[typeLabel="CaseDocument"]:selected, node[typeLabel="LogEvent"]:selected',
      style: {
        width: 10,
        height: 10,
        'border-width': 2,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // MULTI-SELECTION HIGHLIGHT
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.multi-selected',
      style: {
        'border-color': '#22c55e',
        'border-width': 4,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SEARCH HIGHLIGHT
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.search-highlight',
      style: {
        'border-color': '#fbbf24',
        'border-width': 4,
        'overlay-color': '#fbbf24',
        'overlay-padding': 8,
        'overlay-opacity': 0.3,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // PATH HIGHLIGHTING
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.path-highlight',
      style: {
        'border-color': theme === 'dark' ? '#00ff41' : '#16a34a',
        'border-width': 5,
        'overlay-color': theme === 'dark' ? '#00ff41' : '#16a34a',
        'overlay-padding': 10,
        'overlay-opacity': 0.4,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // GROUP/ENTITY HIGHLIGHTING
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.group-highlighted',
      style: {
        'border-color': '#3b82f6',
        'border-width': 4,
        'overlay-color': '#3b82f6',
        'overlay-padding': 8,
        'overlay-opacity': 0.3,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // WORKFLOW HIGHLIGHTING
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.workflow-highlighted',
      style: {
        'border-color': '#3b82f6',
        'border-width': 4,
        'overlay-color': '#3b82f6',
        'overlay-padding': 6,
        'overlay-opacity': 0.25,
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // FADED/FILTERED NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.faded',
      style: { opacity: 0.2 },
    },
    {
      selector: 'node.semi-faded',
      style: { opacity: 0.5 },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // FOCUS DIMMING (selection-based, separate from filter-based .faded)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.focus-dimmed',
      style: { opacity: 0.15 },
    },
    {
      selector: 'edge.focus-dimmed',
      style: { opacity: 0.08 },
    },
    // Compound cluster boxes are layout containers, not entities — never dim them
    // even if the blanket focus-dim pass tagged them.
    {
      selector: 'node[typeLabel="CaseCompound"].focus-dimmed',
      style: { opacity: 1 },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // TIMELINE DIMMING (playhead-driven temporal ghost state)
    // Entities/edges whose timestamp is in the future relative to the playhead
    // are rendered at reduced opacity and "light up" as the cursor passes them.
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.timeline-dimmed',
      style: {
        opacity: 0.25,
        'transition-property': 'opacity',
        'transition-duration': '0.3s',
      },
    },
    {
      selector: 'edge.timeline-dimmed',
      style: {
        opacity: 0.15,
        'transition-property': 'opacity, line-opacity',
        'transition-duration': '0.3s',
      },
    },
    {
      selector: 'node[typeLabel="CaseCompound"].timeline-dimmed',
      style: { opacity: 1 },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // EDGE STYLES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'edge',
      style: {
        width: 1.5,
        'line-color': c.edgeColor,
        'target-arrow-color': c.edgeColor,
        'target-arrow-shape': 'triangle',
        // Parallel edges between the same pair of nodes (in either direction)
        // fan out into separate arcs instead of stacking on one line with their
        // labels and arrowheads on top of each other. A lone edge gets a
        // zero-offset control point, so it still renders perfectly straight.
        'curve-style': 'bezier',
        'control-point-step-size': 60,
        'arrow-scale': 1,
        'line-style': 'solid',
        'line-opacity': 0.6,
        label: '',
        'font-size': '9px',
        'text-rotation': 'autorotate',
        'text-margin-y': -8,
        color: c.edgeLabelColor,
        'text-background-color': c.edgeLabelBg,
        'text-background-opacity': 0.8,
        'text-background-padding': 2,
        'transition-property': 'line-color, width, opacity',
        'transition-duration': '0.15s',
      },
    },

    {
      selector:
        'edge[type = "FROM_CHUNK"], edge[type = "NEXT_CHUNK"], edge[type = "FROM_DOCUMENT"], edge[type = "HAS_CHUNK"], edge[type = "HAS_DOCUMENT"]',
      style: {
        'line-opacity': 0.2,
        width: 1,
        'line-style': 'dashed',
      },
    },

    // A passage's tether to its recording. It states no fact of the case and
    // always runs the same way, and a recording has dozens: a thin line with
    // no arrowhead, so the ring stays lighter than the edges that are findings.
    {
      selector: 'edge[type = "HAS_PASSAGE"]',
      style: {
        width: 1,
        'line-opacity': 0.35,
        'target-arrow-shape': 'none',
      },
    },

    {
      selector: 'edge.hover, edge:active',
      style: {
        label: 'data(type)',
        'line-opacity': 1,
        width: 2.5,
      },
    },

    {
      selector: 'edge.zoomed-in',
      style: {
        label: 'data(type)',
        'line-opacity': 0.8,
      },
    },

    // POSSIBLE_MATCH - Red dashed
    {
      selector: 'edge[type = "POSSIBLE_MATCH"]',
      style: {
        width: 2,
        'line-color': '#ef4444',
        'target-arrow-color': '#ef4444',
        'line-style': 'dashed',
      },
    },

    // HAS_FINDING - Yellow dashed
    {
      selector: 'edge[type = "HAS_FINDING"]',
      style: {
        width: 2,
        'line-color': '#eab308',
        'target-arrow-color': '#eab308',
        'line-style': 'dashed',
      },
    },

    // Relationship type colors (work in both themes)
    {
      selector: 'edge[type = "HAS_SUBSIDIARY"], edge[type = "OWNS"], edge[type = "PARENT_OF"]',
      style: {
        'line-color': '#3b82f6',
        'target-arrow-color': '#3b82f6',
      },
    },
    {
      selector: 'edge[type = "WORKS_FOR"], edge[type = "EMPLOYEE_OF"], edge[type = "EMPLOYED_BY"]',
      style: {
        'line-color': '#10b981',
        'target-arrow-color': '#10b981',
      },
    },
    {
      selector: 'edge[type = "LIVES_AT"], edge[type = "LOCATED_AT"], edge[type = "ADDRESS"]',
      style: {
        'line-color': '#f59e0b',
        'target-arrow-color': '#f59e0b',
      },
    },
    {
      selector:
        'edge[type = "CONNECTED_TO"], edge[type = "RELATED_TO"], edge[type = "ASSOCIATED_WITH"]',
      style: {
        'line-color': '#8b5cf6',
        'target-arrow-color': '#8b5cf6',
      },
    },
    {
      selector:
        'edge[type = "MENTIONED_IN"], edge[type = "APPEARS_IN"], edge[type = "CITED_IN"], edge[type = "DEPICTS"]',
      style: {
        'line-color': '#ec4899',
        'target-arrow-color': '#ec4899',
      },
    },
    // Face provenance stays visible: APPEARS_IN ties the crop to its document
    // and DEPICTS to the matched person, both drawn a touch heavier than the
    // default edge so a crop's two links read at a glance.
    {
      selector: 'edge[type = "APPEARS_IN"], edge[type = "DEPICTS"]',
      style: {
        width: 2,
      },
    },
    {
      selector: 'edge[type = "TRANSACTED_WITH"], edge[type = "PAID"], edge[type = "RECEIVED"]',
      style: {
        'line-color': '#14b8a6',
        'target-arrow-color': '#14b8a6',
      },
    },

    // Edge hover
    {
      selector: 'edge:active',
      style: {
        width: 3,
        'line-color': c.edgeLabelColor,
        'target-arrow-color': c.edgeLabelColor,
      },
    },

    // Edge selected
    {
      selector: 'edge:selected',
      style: {
        width: 3,
        'line-color': '#3b82f6',
        'target-arrow-color': '#3b82f6',
        'overlay-color': '#3b82f6',
        'overlay-padding': 3,
        'overlay-opacity': 0.2,
      },
    },

    // Node-selection blue accent on directly connected edges.
    // Applied by useCytoscapeSelection when a node is clicked. Replaces the
    // previous blanket focus-dim approach so the timeline-driven dim state
    // remains visible alongside selection highlight.
    {
      selector: 'edge.selection-highlight',
      style: {
        width: 3,
        'line-color': '#3b82f6',
        'target-arrow-color': '#3b82f6',
        'line-opacity': 1,
        'overlay-color': '#3b82f6',
        'overlay-padding': 4,
        'overlay-opacity': 0.2,
      },
    },

    // Path highlighted edges
    {
      selector: 'edge.path-highlight',
      style: {
        width: 4,
        'line-color': theme === 'dark' ? '#00ff41' : '#16a34a',
        'target-arrow-color': theme === 'dark' ? '#00ff41' : '#16a34a',
        'overlay-color': theme === 'dark' ? '#00ff41' : '#16a34a',
        'overlay-padding': 4,
        'overlay-opacity': 0.3,
      },
    },

    // Faded edges
    {
      selector: 'edge.faded',
      style: { opacity: 0.1 },
    },
    {
      selector: 'edge.semi-faded',
      style: { opacity: 0.3 },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // AGGREGATED EDGES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'edge[?aggregated]',
      style: {
        width: 3,
        'line-color': '#a855f7',
        'target-arrow-color': '#a855f7',
        'line-style': 'dashed',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // PARENT NODES (Compound nodes)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: ':parent',
      style: {
        'background-opacity': 0.2,
        'background-color': c.parentBg,
        'border-color': c.parentBorder,
        'border-width': 2,
        shape: 'roundrectangle',
        'text-valign': 'top',
        'text-halign': 'center',
        'font-size': '12px',
        'font-weight': 700,
        color: c.parentText,
        'padding-top': '10px',
        'padding-left': '10px',
        'padding-bottom': '10px',
        'padding-right': '10px',
      },
    },

    // Case Compound
    {
      selector: 'node[typeLabel="CaseCompound"]',
      style: {
        'background-opacity': 0.12,
        'background-color': c.caseCompoundBg,
        'border-color': c.caseCompoundBorder,
        'border-width': 2,
        'border-style': 'dashed',
        label: 'data(displayName)',
        'text-valign': 'top',
        'text-halign': 'center',
        'text-margin-y': 10,
        'font-size': '13px',
        'font-weight': 700,
        color: c.caseCompoundText,
        'text-background-color': c.caseCompoundTextBg,
        'text-background-opacity': 0.9,
        'text-background-padding': 6,
        'text-background-shape': 'roundrectangle',
        'padding-top': '25px',
        'padding-left': '20px',
        'padding-bottom': '20px',
        'padding-right': '20px',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // FILTERED OUT / HIDDEN NODES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.filtered-out',
      style: { display: 'none' },
    },
    {
      selector: 'edge.filtered-out',
      style: { display: 'none' },
    },
    {
      selector: 'node.hidden',
      style: { display: 'none' },
    },
    {
      selector: 'edge.hidden',
      style: { display: 'none' },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // LOD (Level of Detail) STYLES
    // ═══════════════════════════════════════════════════════════════════════════
    {
      selector: 'node.lod-hide-labels',
      style: {
        label: '',
        'text-opacity': 0,
      },
    },
    {
      selector: 'edge.lod-hide-edge-labels',
      style: {
        label: '',
        'text-opacity': 0,
      },
    },
    {
      selector: 'node.lod-hide-icons',
      style: {
        'background-image': 'none',
      },
    },
    {
      selector: 'edge.lod-show-edge-labels',
      style: {
        label: 'data(type)',
        'line-opacity': 0.8,
      },
    },
    {
      selector: 'node.lod-show-type-label',
      style: {
        label: 'data(typeLabel)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 8,
        'font-size': '10px',
        'font-weight': 600,
        color: c.lodTypeLabelColor,
        'text-background-color': c.lodTypeLabelBg,
        'text-background-opacity': 0.85,
        'text-background-padding': 3,
        'text-background-shape': 'roundrectangle',
        'text-wrap': 'ellipsis',
        'text-max-width': '100px',
      },
    },

    // Don't override labels for nodes that already have their own labels
    {
      selector: 'node.lod-show-type-label[typeLabel="Event"]',
      style: { label: 'data(dateLabel)' },
    },
    {
      // Sticky notes/Note nodes carry their text INSIDE the rectangle by
      // design (matches a real sticky note). The generic `lod-show-type-label`
      // rule pushes labels below the node — we must restore the in-box
      // positioning from the base Note style here, otherwise the text moves
      // outside as soon as LOD activates.
      selector:
        'node.lod-show-type-label[typeLabel="Note"], node.lod-show-type-label[typeLabel="StickyNote"]',
      style: {
        label: 'data(displayName)',
        'text-valign': 'center',
        'text-halign': 'center',
        'text-margin-y': 0,
        'font-size': '11px',
        'font-weight': 600,
        color: '#78350f',
        'text-background-opacity': 0,
        'text-wrap': 'wrap',
        'text-max-width': '80px',
      },
    },
    {
      selector: 'node.lod-show-type-label[typeLabel="Finding"]',
      style: { label: 'data(displayName)' },
    },
    {
      // A passage's label is its span, inside the chip. The type label rule
      // would print "TranscriptPassage" under it and cut a long span short.
      selector: 'node.lod-show-type-label[typeLabel="TranscriptPassage"]',
      style: passageLabelStyle(c.eventTextColor),
    },
    {
      selector: 'node.lod-show-type-label[typeLabel="Document"]',
      style: { label: 'data(shortDocLabel)' },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NAME LABELS (opt-in)
    // ═══════════════════════════════════════════════════════════════════════════
    // The case graph labels entity nodes with their TYPE and keeps names in
    // hover popups. Report-style graphs (OSINT) are read by name instead, so
    // they opt every node into this class.
    //
    // It has to live in the stylesheet rather than be appended at runtime with
    // `cy.style().selector(...)`: the theme pass in useCytoscapeInstance calls
    // `cy.style().fromJson(createSpectreStylesheet(theme))`, which discards any
    // appended rule (the class stays on the node, so the loss is silent).
    // Declared last so it also outranks the zoom-driven LOD label rules above —
    // including `lod-hide-labels`, whose `text-opacity: 0` would otherwise make
    // the name invisible whenever the graph is fitted below 0.42 zoom.
    {
      selector: 'node.show-name-label, node[typeLabel="SourceSnippet"]',
      style: {
        label: 'data(displayName)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 8,
        'font-size': '14px',
        'font-weight': 600,
        color: c.text,
        'text-opacity': 1,
        'text-background-color': c.lodTypeLabelBg,
        'text-background-opacity': 0.85,
        'text-background-padding': 3,
        'text-background-shape': 'roundrectangle',
        'text-wrap': 'ellipsis',
        'text-max-width': '150px',
      },
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // EDGE LABELS THAT OUTRANK THE ZOOM RULES (declared last, same reason as above)
    // ═══════════════════════════════════════════════════════════════════════════
    // Hovering an edge is a direct question — "what is this line?" — and it was
    // going unanswered below 0.42 zoom: `lod-hide-edge-labels` is declared after
    // the `edge.hover` rule further up, so its `text-opacity: 0` won exactly when
    // the graph is fitted and the labels are needed most. Re-declaring it here
    // puts the answer back.
    {
      selector: 'edge.hover, edge:active',
      style: {
        label: 'data(type)',
        'text-opacity': 1,
        'line-opacity': 1,
        width: 2.5,
      },
    },
    // The promotion chain is the one set of edges whose type has to be readable
    // without being asked for: these are what makes a promoted excerpt evidence
    // rather than a floating node, and they are rare enough (only around
    // promoted ranges) that always labelling them adds no clutter.
    {
      selector: 'edge[type = "MINED_FROM"], edge[type = "EXCERPT_OF"]',
      style: {
        label: 'data(type)',
        'text-opacity': 1,
        'line-opacity': 0.9,
        color: c.edgeLabelColor,
        'text-background-color': c.edgeLabelBg,
        'text-background-opacity': 0.85,
        'text-background-padding': 2,
      },
    },
  ]
}
